import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, PiggyBank, Trophy, ShoppingCart, Activity, RefreshCw, Loader2, Receipt, Store, Link2, Trash2 } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import client, { money, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Tile = ({ icon: Icon, label, value, testId, dark }) => (
  <div data-testid={testId} className={`rounded-2xl p-4 pp-shadow ${dark ? "bg-pp-forest text-white" : "bg-white"}`}>
    <Icon className={`h-5 w-5 ${dark ? "text-pp-gold" : "text-pp-forest"}`} />
    <p className="mt-2 text-2xl font-extrabold">{value}</p>
    <p className={`text-xs ${dark ? "text-white/75" : "text-pp-muted"}`}>{label}</p>
  </div>
);

function PriceSync({ last, reload }) {
  const [busy, setBusy] = useState(false);
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({ retailer_code: "", product_id: "", url: "" });
  const loadSources = () => client.get("/admin/price-sources").then((r) => setSources(r.data)).catch(() => {});
  useEffect(() => { loadSources(); }, []);
  const run = async () => {
    setBusy(true);
    try { await client.post("/admin/price-sync/run"); toast.success("Price refresh started — this takes a minute"); setTimeout(reload, 8000); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const add = async () => {
    try { await client.post("/admin/price-sources", form); setForm({ retailer_code: "", product_id: "", url: "" }); loadSources(); toast.success("Website source added"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const f = last?.flyers || {};
  return (
    <div className="bg-white rounded-3xl p-5 pp-shadow space-y-4" data-testid="price-sync-panel">
      <div className="flex items-center justify-between gap-3">
        <div><h2 className="font-extrabold text-pp-text flex items-center gap-2"><RefreshCw className="h-5 w-5 text-pp-forest" /> Price refresh</h2>
          <p className="text-xs text-pp-muted">Runs automatically every 4 hours · flyers, store websites and receipts</p></div>
        <Button onClick={run} disabled={busy} data-testid="run-price-sync" className="rounded-full bg-pp-forest">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run now"}</Button>
      </div>
      {last ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm" data-testid="last-sync">
          {[["Status", last.status], ["Last run", (last.finished_at || last.started_at || "").slice(0, 16).replace("T", " ")],
            ["Flyer matches", f.flyer_matches ?? "—"], ["Prices updated", f.prices_updated ?? "—"], ["Photos updated", f.images_updated ?? "—"],
            ["Expired sales cleared", last.expired_promos ?? "—"], ["Receipt prices (24h)", last.receipt_prices_24h ?? "—"], ["Trigger", last.trigger]].map(([k, v]) => (
            <div key={k} className="rounded-xl bg-pp-cream p-2.5"><p className="text-[11px] text-pp-muted">{k}</p><p className="font-bold text-pp-text capitalize">{String(v)}</p></div>
          ))}
        </div>
      ) : <p className="text-sm text-pp-muted">No refresh has run yet.</p>}
      <div className="border-t border-pp-border pt-3 space-y-2">
        <p className="text-sm font-bold text-pp-text flex items-center gap-1.5"><Link2 className="h-4 w-4" /> Store website product pages</p>
        <p className="text-xs text-pp-muted">Add a product page URL; each refresh reads its listed price. Many grocers block automated reads — status shows here.</p>
        <div className="grid sm:grid-cols-[110px_1fr_1.4fr_auto] gap-2">
          <Input placeholder="store code" value={form.retailer_code} onChange={(e) => setForm({ ...form, retailer_code: e.target.value })} className="bg-white" data-testid="source-retailer" />
          <Input placeholder="product id" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} className="bg-white" data-testid="source-product" />
          <Input placeholder="https://…" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="bg-white" data-testid="source-url" />
          <Button onClick={add} variant="outline" className="rounded-full bg-white" data-testid="add-source">Add</Button>
        </div>
        {sources.map((s) => (
          <div key={s.id} className="flex items-center gap-2 text-xs rounded-xl bg-pp-cream p-2">
            <span className="font-bold">{s.retailer_code}</span><span className="truncate flex-1">{s.product_name} · {s.url}</span>
            <span className="text-pp-muted">{s.last_status}</span>
            <button onClick={() => client.delete(`/admin/price-sources/${s.id}`).then(loadSources)} className="text-pp-error"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminOverview() {
  const [o, setO] = useState(null);
  const load = () => client.get("/admin/overview").then((r) => setO(r.data)).catch(() => toast.error("Could not load overview"));
  useEffect(() => { load(); }, []);
  if (!o) return <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-pp-forest" /></div>;
  return (
    <div className="space-y-5" data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={PiggyBank} label="Saved for shoppers (all time)" value={money(o.total_saved)} testId="ov-total-saved" dark />
        <Tile icon={Activity} label="Active today" value={o.active_today} testId="ov-active-today" />
        <Tile icon={Users} label="Active last 7 / 30 days" value={`${o.active_7d} / ${o.active_30d}`} testId="ov-active-7d" />
        <Tile icon={Users} label={`Total users · ${o.new_today} new today`} value={o.total_users} testId="ov-total-users" />
        <Tile icon={ShoppingCart} label="Completed trips" value={o.completed_trips} testId="ov-trips" />
        <Tile icon={PiggyBank} label="Avg saved per trip" value={money(o.avg_saved_per_trip)} testId="ov-avg-saved" />
        <Tile icon={Receipt} label="Receipts scanned" value={o.receipts} testId="ov-receipts" />
        <Tile icon={RefreshCw} label="Prices learned from receipts" value={o.receipt_learned_prices} testId="ov-learned" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-3xl p-5 pp-shadow" data-testid="ov-chart">
          <h2 className="font-extrabold text-pp-text">Last 14 days</h2>
          <div className="h-60 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={o.series} margin={{ left: -16, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2EAD8" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#66756F", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#66756F", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip />
                <Area type="monotone" dataKey="active" name="Active users" stroke="#075B3A" fill="#075B3A" fillOpacity={0.15} strokeWidth={2} />
                <Area type="monotone" dataKey="saved" name="Saved ($)" stroke="#F5BE41" fill="#F5BE41" fillOpacity={0.2} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-3xl p-5 pp-shadow space-y-3" data-testid="ov-highlights">
          <h2 className="font-extrabold text-pp-text flex items-center gap-2"><Trophy className="h-5 w-5 text-pp-gold" /> Today's highlights</h2>
          <div className="rounded-2xl bg-pp-cream p-3"><p className="text-xs text-pp-muted">Saved today</p><p className="font-extrabold text-pp-text">{money(o.today.saved)} across {o.today.trips} trips</p></div>
          <div className="rounded-2xl bg-pp-cream p-3"><p className="text-xs text-pp-muted">Top saver today</p><p className="font-extrabold text-pp-text">{o.today.top_saver ? `${o.today.top_saver.name} · ${money(o.today.top_saver.saved)}` : "No trips yet today"}</p></div>
          <div className="rounded-2xl bg-pp-cream p-3"><p className="text-xs text-pp-muted">Biggest single-trip saving</p><p className="font-extrabold text-pp-text">{o.biggest_trip_saving ? `${o.biggest_trip_saving.name} · ${money(o.biggest_trip_saving.saved)}` : "—"}</p></div>
          <div className="rounded-2xl bg-[#EBF7EE] p-3 text-sm text-pp-forest font-semibold" data-testid="ov-promo-line">
            Share-ready: “Pantry Pilot has saved shoppers {money(o.total_saved)} so far.”
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-white rounded-3xl p-5 pp-shadow"><h2 className="font-extrabold text-pp-text flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-pp-forest" /> Most-added items</h2>
          {o.top_items.map((t) => <div key={t.name} className="flex justify-between text-sm py-1.5 border-b border-pp-border last:border-0"><span className="capitalize">{t.name}</span><b>{t.count}</b></div>)}</div>
        <div className="bg-white rounded-3xl p-5 pp-shadow"><h2 className="font-extrabold text-pp-text flex items-center gap-2"><Store className="h-5 w-5 text-pp-forest" /> Most-visited stores</h2>
          {o.top_stores.map((t) => <div key={t.name} className="flex justify-between text-sm py-1.5 border-b border-pp-border last:border-0"><span>{t.name}</span><b>{t.count}</b></div>)}</div>
      </div>

      <PriceSync last={o.last_price_sync} reload={load} />
    </div>
  );
}
